import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ShieldOff, Send, Loader2, Lock, Heart, AlertCircle,
  Eye, MessageSquare, Clock, Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AnonymousWallPage({ embedded = false }) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [viewMode, setViewMode] = useState('submit'); // submit | admin

  // Check if user is admin/manager
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role_id === 'admin' || user.role_id === 'manager' || user.role_id === 'developer' || user.role === 'admin';

  useEffect(() => {
    if (isAdmin && viewMode === 'admin') fetchFeedbacks();
  }, [viewMode]);

  const fetchFeedbacks = async () => {
    setLoadingFeedbacks(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/anonymous-feedback`, { headers: { Authorization: `Bearer ${token}` } });
      setFeedbacks(response.data.feedbacks);
    } catch (error) { toast.error('Failed to load feedback'); }
    finally { setLoadingFeedbacks(false); }
  };

  const handleSubmit = async () => {
    if (!message.trim()) { toast.error('Please enter your message'); return; }
    if (message.trim().length < 10) { toast.error('Message too short. Minimum 10 characters.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/anonymous-feedback`, { message: message.trim() });
      toast.success('Your feedback has been submitted anonymously!');
      setMessage('');
      setSubmitted(true);
    } catch (error) { toast.error('Failed to submit feedback'); }
    finally { setSubmitting(false); }
  };

  const adminToggle = isAdmin && (
    <div className="flex gap-2">
      <Button variant={viewMode === 'submit' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('submit')} className={viewMode === 'submit' ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white border-0' : 'border-[hsl(var(--border))]'}>
        <Send size={14} className="mr-1.5" /> Submit
      </Button>
      <Button variant={viewMode === 'admin' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('admin')} className={viewMode === 'admin' ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white border-0' : 'border-[hsl(var(--border))]'} data-testid="admin-view-btn">
        <Eye size={14} className="mr-1.5" /> View Feedback ({feedbacks.length || '...'})
      </Button>
    </div>
  );

  const content = (
    <>
      {!embedded && (
        <SectionHeader icon={ShieldOff} title="Anonymous Feedback Wall" subtitle="Share your thoughts anonymously" iconBg="bg-gradient-to-br from-purple-500 to-pink-600 shadow-purple-500/20" action={adminToggle} />
      )}
      {embedded && adminToggle && <div className="flex justify-end mb-4">{adminToggle}</div>}

        {viewMode === 'submit' ? (
          <div className="max-w-2xl mx-auto">
            {/* Privacy Notice */}
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Lock size={16} className="text-purple-400" />
                </div>
                <div>
                  <h3 className="font-medium text-purple-400 mb-1 text-sm">100% Anonymous & Secure</h3>
                  <p className="text-xs text-[hsl(var(--text-muted))]">
                    Your feedback is not linked to any account. Nobody will know who wrote it. Only management can view feedback.
                  </p>
                </div>
              </div>
            </div>

            {/* Submission Form */}
            <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-6">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 rounded-2xl flex items-center justify-center mb-4">
                    <Heart size={28} className="text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-[hsl(var(--text-primary))] mb-2">Thank You!</h3>
                  <p className="text-sm text-[hsl(var(--text-muted))] mb-6">Your feedback has been received and will be considered.</p>
                  <Button variant="outline" onClick={() => setSubmitted(false)} className="border-[hsl(var(--border))]">Submit Another</Button>
                </div>
              ) : (
                <>
                  <div className="mb-5">
                    <label className="text-sm text-[hsl(var(--text-secondary))] mb-2 block font-medium">What would you like to share?</label>
                    <textarea 
                      data-testid="anonymous-message-input"
                      value={message} onChange={(e) => setMessage(e.target.value)}
                      placeholder="Write your thoughts, suggestions, complaints, or anything...&#10;&#10;Contoh:&#10;- Suggestions to improve workflow&#10;- Feedback about management&#10;- Ideas for the company"
                      rows={8}
                      className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4 text-sm text-[hsl(var(--text-primary))] placeholder:text-[hsl(var(--text-muted))] resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                    />
                    <div className="flex justify-between mt-2">
                      <span className={`text-xs ${message.length < 10 ? 'text-[hsl(var(--text-muted))]' : 'text-emerald-400'}`}>
                        {message.length} / minimum 10 characters
                      </span>
                    </div>
                  </div>
                  <Button data-testid="submit-anonymous-btn" onClick={handleSubmit} disabled={submitting || message.trim().length < 10} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white shadow-lg shadow-purple-500/20">
                    {submitting ? <><Loader2 size={16} className="animate-spin mr-2" /> Submitting...</> : <><Send size={16} className="mr-2" /> Submit Anonymously</>}
                  </Button>
                  <p className="text-xs text-center text-[hsl(var(--text-muted))] mt-4 flex items-center justify-center gap-1">
                    <Shield size={12} /> Your feedback is protected and confidential
                  </p>
                </>
              )}
            </div>
          </div>
        ) : (
          /* Admin View */
          <div>
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-purple-500 to-pink-600 opacity-5 rounded-bl-full" />
                <div className="flex items-center gap-2 mb-2"><MessageSquare size={14} className="text-[hsl(var(--text-muted))]" /><p className="text-xs text-[hsl(var(--text-muted))]">Total Feedback</p></div>
                <p className="text-2xl font-bold">{feedbacks.length}</p>
              </div>
              <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-blue-500 to-blue-600 opacity-5 rounded-bl-full" />
                <div className="flex items-center gap-2 mb-2"><Clock size={14} className="text-[hsl(var(--text-muted))]" /><p className="text-xs text-[hsl(var(--text-muted))]">This Week</p></div>
                <p className="text-2xl font-bold">{feedbacks.filter(f => { const d = new Date(f.created_at); const now = new Date(); return (now - d) / 86400000 < 7; }).length}</p>
              </div>
              <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-emerald-500 to-emerald-600 opacity-5 rounded-bl-full" />
                <div className="flex items-center gap-2 mb-2"><Clock size={14} className="text-[hsl(var(--text-muted))]" /><p className="text-xs text-[hsl(var(--text-muted))]">Today</p></div>
                <p className="text-2xl font-bold">{feedbacks.filter(f => new Date(f.created_at).toDateString() === new Date().toDateString()).length}</p>
              </div>
            </div>

            {loadingFeedbacks ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-400" /></div>
            ) : feedbacks.length === 0 ? (
              <div className="text-center py-16 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl">
                <MessageSquare size={48} className="mx-auto text-purple-500/30 mb-4" />
                <p className="text-[hsl(var(--text-secondary))]">No anonymous feedback yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {feedbacks.map((fb, idx) => (
                  <div key={fb.id || idx} data-testid={`feedback-${idx}`} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5 hover:border-purple-500/20 transition-all">
                    <p className="text-[hsl(var(--text-primary))] text-sm leading-relaxed mb-3">{fb.message}</p>
                    <div className="flex items-center gap-2 text-xs text-[hsl(var(--text-muted))]">
                      <Clock size={12} />
                      <span>{new Date(fb.created_at).toLocaleString('ms-MY', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                      <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-[10px] ml-2">Anonymous</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </>
    );

  if (embedded) return content;

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        {content}
      </div>
    </ResponsiveLayout>
  );
}
